import { useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FAQ, { faqJsonLd, faqJsonLdEn } from "@/components/FAQ";
import { useLang, useT, useLocalePath } from "@/i18n";
import { useSeo, absoluteUrl } from "@/lib/seo";

const Faq = () => {
  const lang = useLang();
  const lp = useLocalePath();
  const t = useT(
    {
      title: "Häufige Fragen zur Komplementärtherapie | PRAXIS IB Wien",
      description:
        "Antworten zu Ablauf, Kosten, Diagnostik, Labor und Beschwerdebildern der komplementärtherapeutischen Begleitung in der PRAXIS IB in Wien.",
      h1: "Häufige Fragen zur Begleitung in der PRAXIS IB",
      intro:
        "Hier finden Sie ausführliche Antworten zu Arbeitsweise, Ablauf der Begleitung, Diagnostik und häufigen Beschwerdebildern. Wenn Ihre Frage offen bleibt, beantwortet sie Mikrobi, der Begleiter auf dieser Seite, oder Sie schreiben direkt an office@ibpraxis.net.",
      home: "Startseite",
      faq: "Häufige Fragen",
      breadcrumbLabel: "Brotkrumen",
      inLanguage: "de-AT",
      complaints: "Beschwerdebilder ansehen",
      booking: "Termin buchen",
    },
    {
      title: "Frequently asked questions | PRAXIS IB Vienna",
      description:
        "Answers on process, fees, diagnostics, laboratory work and conditions treated in complementary therapy at PRAXIS IB in Vienna.",
      h1: "Frequently asked questions about care at PRAXIS IB",
      intro:
        "Here you will find detailed answers on the approach, the course of care, diagnostics and common conditions. If your question remains open, Mikrobi, the guide on this site, will answer it – or simply write to office@ibpraxis.net.",
      home: "Home",
      faq: "FAQ",
      breadcrumbLabel: "Breadcrumb",
      inLanguage: "en",
      complaints: "See health concerns",
      booking: "Book an appointment",
    },
  );

  const jsonLd = useMemo(
    () => [
      lang === "en" ? faqJsonLdEn : faqJsonLd,
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t.home, item: absoluteUrl(lp("/")) },
          { "@type": "ListItem", position: 2, name: t.faq, item: absoluteUrl(lp("/faq")) },
        ],
      },
    ],
    [lang, lp, t.home, t.faq],
  );

  useSeo({
    title: t.title,
    description: t.description,
    canonical: "/faq",
    jsonLd,
    jsonLdId: "faq-page",
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-28 pb-6 md:pt-32">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <nav aria-label={t.breadcrumbLabel} className="mb-4 text-sm text-muted-foreground">
                <Link to={lp("/")} className="hover:text-foreground">
                  {t.home}
                </Link>
                <span className="mx-2">/</span>
                <span className="text-foreground">{t.faq}</span>
              </nav>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">{t.h1}</h1>
              <p className="text-muted-foreground leading-relaxed">{t.intro}</p>
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <Link to={lp("/beschwerden")} className="text-primary underline underline-offset-4 hover:no-underline">
                  {t.complaints}
                </Link>
                <Link to={lp("/termin")} className="text-primary underline underline-offset-4 hover:no-underline">
                  {t.booking}
                </Link>
              </div>
            </div>
          </div>
        </section>
        <FAQ />
      </main>
      <Footer />
    </div>
  );
};

export default Faq;
